import React, { useEffect } from 'react';
import Hero from '../components/Hero';

function About() {
  useEffect(() => {
    // Animation on scroll
    const animateOnScroll = () => {
      const elements = document.querySelectorAll('.value-card, .story-block, .founders-image');
      
      elements.forEach((element, index) => {
        const elementPosition = element.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        
        if (elementPosition < windowHeight - 50) {
          element.style.opacity = '1';
          element.style.transform = 'translateY(0)';
        }
      });
    };
    
    // Set initial state for animation elements
    const elements = document.querySelectorAll('.value-card, .story-block, .founders-image');
    elements.forEach((element, index) => {
      element.style.opacity = '0';
      element.style.transform = 'translateY(30px)';
      element.style.transition = `opacity 0.6s ease ${index * 0.1}s, transform 0.6s ease ${index * 0.1}s`;
    });
    
    // Run animation check on load and scroll
    setTimeout(animateOnScroll, 100);
    window.addEventListener('scroll', animateOnScroll);
    
    return () => {
      window.removeEventListener('scroll', animateOnScroll);
    };
  }, []);

  return (
    <main>
      <Hero 
        variant="about"
        title="OUR STORY"
        tagline='TWO PEOPLE. ONE DREAM. <span class="and">ENDLESS</span> COFFEE.'
        showSocial={false}
      />

      {/* Meet The Founders Section */}
      <section className="founders-section">
        <div className="founders-container">
          <div className="founders-image">
            <img src="/images/Bryce Zoe Black and White.JPG" alt="Bryce and Zoe - Founders of Grounded Coffee Co." />
            <div className="founders-badge">Est. 2024</div>
          </div>
          <div className="founders-text">
            <h2>MEET BRYCE <span className="and">&</span> ZOE</h2>
            <p className="founders-intro">
              We're just two people who believe that if you're going to do something, you might as well give it everything you've got.
            </p>
            <p>
              With Bryce's 4 years of business experience and Zoe's 3 years behind the espresso machine, we combined our skills to create something we're truly proud of. Grounded Coffee Co. isn't just a business — it's our shared passion poured into every cup.
            </p>
            <p className="highlight-text">
              We put everything into creating coffee that <strong>you will obsess over.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* In Action Section */}
      <section className="in-action-section">
        <h2>BEHIND THE WINDOW</h2>
        <div className="action-grid">
          <div className="action-card">
            <img src="/images/Zoe Making Drinks.JPG" alt="Zoe crafting drinks at the espresso machine" />
            <div className="action-overlay">
              <h3>Zoe</h3>
            </div>
          </div>
          <div className="action-card">
            <img src="/images/Bryce Handing Drinks.JPG" alt="Bryce serving customers at the trailer window" />
            <div className="action-overlay">
              <h3>Bryce</h3>
            </div>
          </div>
        </div>
      </section>

      {/* The Story Section */}
      <section className="story-section">
        <h2>HOW IT ALL STARTED</h2>
        <div className="story-timeline">
          <div className="story-block">
            <div className="story-icon">
              <i className="fas fa-heart"></i>
            </div>
            <div className="story-content">
              <h3>The Beginning</h3>
              <p>We met at the gym — two people focused on health, growth, and pushing ourselves to be better. Little did we know, that same drive would lead us to build something incredible together.</p>
            </div>
          </div>
          
          <div className="story-block">
            <div className="story-icon">
              <i className="fas fa-lightbulb"></i>
            </div>
            <div className="story-content">
              <h3>The Name</h3>
              <p>Fun fact: Zoe's mom came up with "Grounded" and it just stuck. Sometimes the best ideas come from the people who know you best. Thanks, Mom!</p>
            </div>
          </div>
          
          <div className="story-block">
            <div className="story-icon">
              <i className="fas fa-fire"></i>
            </div>
            <div className="story-content">
              <h3>Taking The Leap</h3>
              <p>When Zoe got fired from her barista job for starting our coffee trailer, we knew we were onto something. Sometimes you have to burn the boats to sail somewhere new. No looking back — only forward.</p>
            </div>
          </div>
          
          <div className="story-block">
            <div className="story-icon">
              <i className="fas fa-mug-hot"></i>
            </div>
            <div className="story-content">
              <h3>Today</h3>
              <p>Now we're here, doing what we love every single day. Crafting organic, quality coffee with all our heart — and building a community of people who appreciate the good stuff.</p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Stand For */}
      <section className="values-section">
        <h2>WHAT WE STAND FOR</h2>
        <div className="values-container">
          <div className="value-card">
            <i className="fas fa-medal"></i>
            <h3>Uncompromising Quality</h3>
            <p>We don't cut corners. From bean to cup, every ingredient is carefully selected because you deserve coffee that's crafted, not manufactured.</p>
          </div>
          <div className="value-card">
            <i className="fas fa-heartbeat"></i>
            <h3>Health-Conscious</h3>
            <p>All our espresso, teas, and syrups are organic. We believe great taste and wellness go hand in hand — so you can enjoy your daily ritual guilt-free.</p>
          </div>
          <div className="value-card">
            <i className="fas fa-hand-holding-heart"></i>
            <h3>Made With Love</h3>
            <p>This isn't just a job for us — it's our dream. Every drink we make carries a piece of our story and our commitment to making your day a little better.</p>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="quote-section">
        <div className="quote-content">
          <blockquote>
            "If you're going to do anything, you might as well try your best at it — regardless of what it is."
          </blockquote>
          <p className="quote-attribution">— Our Philosophy</p>
        </div>
      </section>

      <section className="cta-section">
        <div className="cta-content">
          <h2>COME SAY HI</h2>
          <p>We'd love to meet you! Follow along on Instagram for our latest creations, locations, and behind-the-scenes moments.</p>
          <a href="https://instagram.com/groundedcoffee_co" className="cta-button" target="_blank" rel="noopener noreferrer">
            <i className="fab fa-instagram"></i> @groundedcoffee_co
          </a>
        </div>
      </section>
    </main>
  );
}

export default About;

